/**
 * Candidate profile vocabulary for the web app — the reader-facing strings and
 * the field order, one string in one place (CANDIDATE_PROFILES_PRD.md §5.1.2,
 * §5.2.4, §5.6.1). Mirrors backend/src/constants/candidateProfiles.js.
 */

export type CoverageTier = 'full' | 'standard';
export type PublicationStatus = 'draft' | 'published';
export type Stance = 'support' | 'oppose' | 'unclear' | 'declined' | 'no_position';
export type ResponseStatus = 'answered' | 'declined' | 'no_response';

export type ProfileField =
  | 'who_they_are'
  | 'why_running'
  | 'top_priorities'
  | 'experience_background'
  | 'record_in_office'
  | 'who_backs_them';

/** The six system fields in the reader's order. */
export const PROFILE_FIELDS: ProfileField[] = [
  'who_they_are',
  'why_running',
  'top_priorities',
  'experience_background',
  'record_in_office',
  'who_backs_them',
];

export const PROFILE_FIELD_LABELS: Record<ProfileField, string> = {
  who_they_are: 'Who they are',
  why_running: 'Why they say they are running',
  top_priorities: 'Top priorities',
  experience_background: 'Experience and background',
  record_in_office: 'Record in office',
  who_backs_them: 'Who backs them',
};

/** `null` tier = no profile row = "Official record only" (§5.2.4). */
export const COVERAGE_LABELS: Record<CoverageTier | 'official_only', string> = {
  full: 'Full profile',
  standard: 'Standard profile',
  official_only: 'Official record only',
};

export const coverageLabel = (tier: CoverageTier | null | undefined): string =>
  COVERAGE_LABELS[tier ?? 'official_only'];

/** Reader-facing stance labels; `unclear` renders the quote instead of a chip. */
export const STANCE_LABELS: Record<Stance, string | null> = {
  support: 'Supports',
  oppose: 'Opposes',
  unclear: null,
  declined: 'Declined to say',
  no_position: 'No position found',
};

export const RESPONSE_STATUS_LABELS: Record<ResponseStatus, string> = {
  answered: 'Answered',
  declined: 'Declined to answer',
  no_response: 'No response',
};

export const POSITION_SOURCE_LABELS: Record<string, string> = {
  questionnaire: 'Questionnaire',
  website: 'Campaign site',
  statement: 'Statement',
  news_quote: 'Quoted in the press',
  referral_vote: 'Recorded vote',
};
